import React, { useState, useEffect, useCallback } from 'react';
import {
  hrPayrollApi,
  type PayrollSummaryDto,
  type PayrollRunDto,
  type PayrollRunDetailDto,
} from '../../services/api';

function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(n);
}

function formatDate(s: string) {
  return new Date(s).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function PayrollPage() {
  const [summary, setSummary] = useState<PayrollSummaryDto | null>(null);
  const [runs, setRuns] = useState<PayrollRunDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [runModalOpen, setRunModalOpen] = useState(false);
  const [detailRun, setDetailRun] = useState<PayrollRunDetailDto | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  const [runForm, setRunForm] = useState({
    period_start: '',
    period_end: '',
    pay_date: '',
  });
  const [runSubmitting, setRunSubmitting] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);

  const fetchPayroll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await hrPayrollApi.getSummaryAndRuns();
      if (res.data) {
        setSummary(res.data.summary);
        setRuns(res.data.runs);
      }
    } catch {
      setSummary(null);
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPayroll();
  }, [fetchPayroll]);

  useEffect(() => {
    if (!detailId) {
      setDetailRun(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const res = await hrPayrollApi.getRun(detailId);
      if (!cancelled && res.data) setDetailRun(res.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [detailId]);

  const openRunModal = () => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const pay = new Date(today.getFullYear(), today.getMonth() + 1, 5);
    setRunForm({
      period_start: start.toISOString().slice(0, 10),
      period_end: end.toISOString().slice(0, 10),
      pay_date: pay.toISOString().slice(0, 10),
    });
    setRunError(null);
    setRunModalOpen(true);
  };

  const submitRunPayroll = async () => {
    setRunSubmitting(true);
    setRunError(null);
    try {
      const res = await hrPayrollApi.runPayroll(runForm);
      if (res.message && !res.data) {
        setRunError(res.message);
        return;
      }
      if (res.errors) {
        setRunError(Object.values(res.errors).flat().join(' '));
        return;
      }
      setRunModalOpen(false);
      await fetchPayroll();
      if (res.data) setDetailId(res.data.id);
    } catch (e) {
      setRunError('Failed to run payroll. Try again.');
    } finally {
      setRunSubmitting(false);
    }
  };

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-700',
      pending_review: 'bg-amber-100 text-amber-800',
      approved: 'bg-emerald-100 text-emerald-800',
      processed: 'bg-blue-100 text-blue-800',
    };
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${map[status] ?? 'bg-gray-100 text-gray-700'}`}
      >
        {status.replace(/_/g, ' ')}
      </span>
    );
  };

  return (
    <div className="max-w-7xl mx-auto w-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payroll</h1>
          <p className="text-sm text-gray-500 mt-0.5">Process payroll and manage compensation.</p>
        </div>
        <button
          type="button"
          onClick={openRunModal}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 transition-colors shrink-0"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
          Run Payroll
        </button>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-gray-500">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 shrink-0">
            <div className="bg-white rounded-xl p-4 shadow-card border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-brand-100 flex items-center justify-center text-brand-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{summary?.employees ?? 0}</p>
                  <p className="text-sm text-gray-500">Employees</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-card border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {summary ? formatCurrency(summary.monthly_payroll) : formatCurrency(0)}
                  </p>
                  <p className="text-sm text-gray-500">Monthly Payroll</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-card border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-brand-100 flex items-center justify-center text-brand-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {summary?.next_pay_date ? formatDate(summary.next_pay_date) : '–'}
                  </p>
                  <p className="text-sm text-gray-500">Next Pay Date</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-card border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{summary?.pending_reviews ?? 0}</p>
                  <p className="text-sm text-gray-500">Pending Reviews</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-card border border-gray-100 flex-1 min-h-0 flex flex-col overflow-hidden">
            <h2 className="text-base font-semibold text-gray-900 px-6 py-4 border-b border-gray-100">
              Payroll Runs
            </h2>
            <div className="flex-1 min-h-0 overflow-auto p-6">
              {runs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mb-4">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="font-semibold text-gray-900">No Payroll Runs Yet</p>
                  <p className="text-sm text-gray-500 mt-1 max-w-sm">
                    Add employees and run your first payroll to get started.
                  </p>
                  <button
                    type="button"
                    onClick={openRunModal}
                    className="mt-4 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                    Run First Payroll
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {runs.map((run) => (
                    <button
                      type="button"
                      key={run.id}
                      onClick={() => setDetailId(detailId === run.id ? null : run.id)}
                      className="w-full flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50/50 hover:bg-gray-50 px-4 py-3 text-left transition-colors"
                    >
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="font-medium text-gray-900">
                          {formatDate(run.period_start)} – {formatDate(run.period_end)}
                        </span>
                        <span className="text-sm text-gray-500">Pay date: {formatDate(run.pay_date)}</span>
                        {statusBadge(run.status)}
                      </div>
                      <span className="font-semibold text-gray-900">{formatCurrency(run.total_net_pay)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {runModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Run Payroll</h3>
            <p className="text-sm text-gray-500 mb-4">
              Set the pay period and pay date. Pay will be calculated for all active employees.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Period start</label>
                <input
                  type="date"
                  value={runForm.period_start}
                  onChange={(e) => setRunForm((f) => ({ ...f, period_start: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Period end</label>
                <input
                  type="date"
                  value={runForm.period_end}
                  onChange={(e) => setRunForm((f) => ({ ...f, period_end: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pay date</label>
                <input
                  type="date"
                  value={runForm.pay_date}
                  onChange={(e) => setRunForm((f) => ({ ...f, pay_date: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
            </div>
            {runError && (
              <p className="mt-3 text-sm text-red-600">{runError}</p>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => !runSubmitting && setRunModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitRunPayroll}
                disabled={runSubmitting || !runForm.period_start || !runForm.period_end || !runForm.pay_date}
                className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
              >
                {runSubmitting ? 'Running…' : 'Run Payroll'}
              </button>
            </div>
          </div>
        </div>
      )}

      {detailRun && detailId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Payroll Run Details</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {formatDate(detailRun.period_start)} – {formatDate(detailRun.period_end)} · Pay date:{' '}
                    {formatDate(detailRun.pay_date)}
                  </p>
                  {statusBadge(detailRun.status)}
                </div>
                <button
                  type="button"
                  onClick={() => setDetailId(null)}
                  className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="mt-3 flex flex-wrap gap-4 text-sm">
                <span className="text-gray-600">
                  Gross: <strong className="text-gray-900">{formatCurrency(detailRun.total_gross_pay)}</strong>
                </span>
                <span className="text-gray-600">
                  Deductions: <strong className="text-gray-900">{formatCurrency(detailRun.total_deductions)}</strong>
                </span>
                <span className="text-gray-600">
                  Net: <strong className="text-gray-900">{formatCurrency(detailRun.total_net_pay)}</strong>
                </span>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-500 font-medium">
                    <th className="pb-2 pr-4">Employee</th>
                    <th className="pb-2 pr-4 text-right">Gross</th>
                    <th className="pb-2 pr-4 text-right">Deductions</th>
                    <th className="pb-2 text-right">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {detailRun.entries.map((e) => (
                    <tr key={e.id} className="border-b border-gray-100">
                      <td className="py-2 pr-4 font-medium text-gray-900">{e.employee_name}</td>
                      <td className="py-2 pr-4 text-right text-gray-700">{formatCurrency(e.gross_pay)}</td>
                      <td className="py-2 pr-4 text-right text-gray-700">{formatCurrency(e.total_deductions)}</td>
                      <td className="py-2 text-right font-medium text-gray-900">{formatCurrency(e.net_pay)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
